import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  FileText,
  Lock,
  RefreshCw,
  ScanLine,
  XCircle,
} from "lucide-react";

import {
  useCreateQuoteRevision,
  useExtractQuote,
  useQuoteDocument,
  useQuoteProvenance,
  useRejectQuote,
  useRetryQuoteExtraction,
  useSupplierQuote,
  useUpdateQuoteLine,
  useValidateQuote,
} from "@/api/quotes";
import type {
  QuoteTotals,
  SupplierQuote,
  SupplierQuoteDetail,
  SupplierQuoteLine,
} from "@/api/types";
import { Button } from "@/components/Button";
import { Dialog } from "@/components/Dialog";
import { TableShell, Td, Th, Tr } from "@/components/DataTable";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/components/toast-context";
import { Page } from "@/layout/Page";
import { PageHeader } from "@/layout/PageHeader";
import { formatDate, formatDateTime, formatQuantity, pluralize } from "@/lib/format";
import {
  extractionTone,
  formatAvailability,
  formatExtractionState,
  formatFreightMode,
  formatLineResponse,
  formatMatchConfidence,
  formatQuoteStatus,
  formatQuoteWarning,
  formatReceivedVia,
  lineResponseTone,
  quoteTone,
} from "@/lib/quote-display";

/**
 * Une soumission fournisseur (V1.6-06).
 *
 * L'écran a une seule promesse à tenir : **ne jamais en dire plus que ce qu'on
 * sait**. Tant que la soumission n'est pas validée, ce qui est affiché est
 * « ce qui a été lu ou saisi », et le bandeau le dit en toutes lettres. Une
 * disponibilité porte la date de la soumission. Un transport non précisé n'est
 * pas gratuit.
 *
 * Le document et les données structurées sont côte à côte sur grand écran —
 * c'est la seule disposition dans laquelle vérifier une extraction est
 * réellement faisable — et l'un au-dessus de l'autre sur téléphone, parce que
 * deux colonnes de 190 px ne sont pas une lecture.
 */
export function SupplierQuoteDetailPage() {
  const { quoteId = "" } = useParams();
  const toast = useToast();
  const { data, isLoading, error, refetch } = useSupplierQuote(quoteId);
  const extract = useExtractQuote();
  const retry = useRetryQuoteExtraction();
  const [validating, setValidating] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!data) return null;

  const { quote, lines, totals, unanswered, needs_review, can_validate } = data;
  const editable = quote.status === "RECEIVED" || quote.status === "UNDER_REVIEW";

  return (
    <Page>
      <PageHeader
        title={quote.supplier_name}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[12px]">{quote.code}</span>
            <StatusBadge tone={quoteTone(quote.status)}>
              {formatQuoteStatus(quote.status)}
            </StatusBadge>
            {quote.extraction_state !== "NOT_STARTED" && (
              <StatusBadge tone={extractionTone(quote.extraction_state)}>
                {formatExtractionState(quote.extraction_state)}
              </StatusBadge>
            )}
            <span className="text-ink-subtle">
              {pluralize(lines.length, "ligne")} · {formatReceivedVia(quote.received_via)}
            </span>
          </span>
        }
        actions={
          <div className="flex flex-wrap gap-2">
            {editable && quote.media_id && (
              <Button
                variant="secondary"
                size="sm"
                disabled={extract.isPending}
                onClick={() =>
                  void extract
                    .mutateAsync({ quoteId })
                    .catch((failure: unknown) =>
                      toast.error("Lecture impossible", String(failure)),
                    )
                }
              >
                <ScanLine className="size-4" aria-hidden />
                Lire le document
              </Button>
            )}
            {editable && quote.extraction_state === "FAILED" && (
              <Button
                variant="secondary"
                size="sm"
                disabled={retry.isPending}
                onClick={() =>
                  void retry
                    .mutateAsync({ quoteId })
                    .catch((failure: unknown) =>
                      toast.error("Relance impossible", String(failure)),
                    )
                }
              >
                <RefreshCw className="size-4" aria-hidden />
                Réessayer la lecture
              </Button>
            )}
            {editable && (
              <>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!can_validate}
                  onClick={() => setValidating(true)}
                >
                  <CheckCircle2 className="size-4" aria-hidden />
                  Valider la soumission
                </Button>
                <Button variant="secondary" size="sm" onClick={() => setRejecting(true)}>
                  <XCircle className="size-4" aria-hidden />
                  Refuser
                </Button>
              </>
            )}
            {!editable && <RevisionButton quoteId={quoteId} quote={quote} />}
          </div>
        }
      />

      <Link
        to="/quotes"
        className="inline-flex items-center gap-1.5 text-[13px] text-ink-subtle hover:text-ink"
      >
        <ArrowLeft className="size-4" aria-hidden />
        Toutes les soumissions
      </Link>

      <StatusNotice quote={quote} needsReview={needs_review} />
      <WarningList warnings={quote.warnings} />

      {/* Document à gauche, données à droite. Sur téléphone, l'un puis l'autre. */}
      <div className="grid min-w-0 gap-4 lg:grid-cols-2">
        <DocumentPanel quoteId={quoteId} quote={quote} />
        <div className="flex min-w-0 flex-col gap-4">
          <Identity quote={quote} />
          <Totals totals={totals} quote={quote} />
        </div>
      </div>

      <LinesSection
        quoteId={quoteId}
        quote={quote}
        lines={lines}
        editable={editable}
      />

      {unanswered.length > 0 && <Unanswered detail={data} />}

      <ProvenancePanel quoteId={quoteId} />

      <ValidateDialog
        quoteId={quoteId}
        detail={data}
        open={validating}
        onOpenChange={setValidating}
      />
      <RejectDialog quoteId={quoteId} open={rejecting} onOpenChange={setRejecting} />
    </Page>
  );
}

/**
 * Ce que l'écran promet, en toutes lettres.
 *
 * « Valeurs extraites — à vérifier » avant validation, et jamais « prix
 * fournisseur confirmé ». Après, la date et le nom de qui a validé.
 */
function StatusNotice({
  quote,
  needsReview,
}: {
  quote: SupplierQuote;
  needsReview: boolean;
}) {
  if (quote.status === "VALIDATED") {
    return (
      <div className="flex flex-col gap-1 rounded-lg border border-success/30 bg-success-subtle p-3 text-[13px]">
        <p className="flex items-center gap-2 font-medium text-ink">
          <Lock className="size-4 shrink-0" aria-hidden />
          Soumission validée le {formatDateTime(quote.validated_at)} par{" "}
          {quote.validated_by}
        </p>
        <p className="text-ink-muted">
          Son contenu est figé. Une correction se fait par une révision, pas en le
          réécrivant. Valider ne veut pas dire « fournisseur choisi » ni « achat
          approuvé ».
        </p>
      </div>
    );
  }

  if (quote.status === "SUPERSEDED") {
    return (
      <div className="rounded-lg border border-border bg-surface-raised p-3 text-[13px] text-ink-muted">
        Cette soumission a été remplacée par une révision le{" "}
        {formatDateTime(quote.superseded_at)}. Elle reste lisible telle qu'elle a été
        validée.
      </div>
    );
  }

  if (quote.status === "REJECTED") {
    return (
      <div className="rounded-lg border border-border bg-surface-raised p-3 text-[13px] text-ink-muted">
        Soumission refusée le {formatDateTime(quote.rejected_at)} par {quote.rejected_by}.
        {quote.rejection_reason ? ` ${quote.rejection_reason}` : ""}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1 rounded-lg border border-warning/30 bg-warning-subtle p-3 text-[13px]">
      <p className="flex items-center gap-2 font-medium text-ink">
        <AlertTriangle className="size-4 shrink-0" aria-hidden />
        Valeurs extraites — à vérifier
      </p>
      <p className="text-ink-muted">
        Rien ici n'est encore utilisable : ni pour comparer, ni pour acheter. Vérifiez
        les lignes contre le document, puis validez.
        {needsReview ? " Le contrôle a relevé des points à regarder." : ""}
      </p>
    </div>
  );
}

function WarningList({ warnings }: { warnings: string[] }) {
  if (warnings.length === 0) return null;
  return (
    <ul className="flex flex-col gap-1 rounded-lg border border-border bg-surface p-3 text-[13px] text-ink-muted">
      {warnings.map((warning) => (
        <li key={warning} className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-warning" aria-hidden />
          {formatQuoteWarning(warning)}
        </li>
      ))}
    </ul>
  );
}

/**
 * Le document, tel que la chaîne documentaire l'a lu.
 *
 * Ce n'est pas un visualiseur de PDF : c'est le texte que la lecture a
 * réellement vu. Vérifier une extraction contre une image du PDF laisserait
 * croire que la machine a lu ce que l'œil voit.
 */
function DocumentPanel({ quoteId, quote }: { quoteId: string; quote: SupplierQuote }) {
  const { data: chunks, isLoading } = useQuoteDocument(quoteId, {
    enabled: Boolean(quote.media_id),
  });

  if (!quote.media_id) {
    return (
      <section className="flex min-w-0 flex-col gap-2">
        <h2 className="text-[15px] font-medium text-ink">Source</h2>
        <div className="rounded-lg border border-border bg-surface p-3 text-[13px] text-ink-muted">
          {quote.received_via === "MANUAL_ENTRY"
            ? "Prix donné de vive voix. Aucun document du fournisseur n'existe — et HD-Vault n'en invente pas."
            : "Aucun document joint à cette soumission."}
        </div>
      </section>
    );
  }

  return (
    <section className="flex min-w-0 flex-col gap-2">
      <h2 className="flex items-center gap-2 text-[15px] font-medium text-ink">
        <FileText className="size-4" aria-hidden />
        Document
      </h2>
      {isLoading && <LoadingState />}
      {chunks && chunks.length === 0 && (
        <div className="rounded-lg border border-border bg-surface p-3 text-[13px] text-ink-muted">
          Le document n'a pas encore été indexé. La lecture attend la passe
          documentaire.
        </div>
      )}
      {chunks && chunks.length > 0 && (
        <pre className="max-h-[32rem] overflow-auto whitespace-pre-wrap rounded-lg border border-border bg-surface-raised p-3 text-[12px] text-ink-muted">
          {chunks.map((chunk) => chunk.content).join("\n\n")}
        </pre>
      )}
    </section>
  );
}

function Identity({ quote }: { quote: SupplierQuote }) {
  return (
    <section className="flex min-w-0 flex-col gap-2">
      <h2 className="text-[15px] font-medium text-ink">Identité</h2>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 rounded-lg border border-border bg-surface p-3 text-[13px]">
        <dt className="text-ink-subtle">Numéro du fournisseur</dt>
        <dd className="text-ink">{quote.quote_number ?? "—"}</dd>
        <dt className="text-ink-subtle">Date de la soumission</dt>
        <dd className="text-ink">
          {quote.quote_date ? formatDate(quote.quote_date) : "—"}
        </dd>
        <dt className="text-ink-subtle">Reçue le</dt>
        <dd className="text-ink">{formatDate(quote.received_at)}</dd>
        <dt className="text-ink-subtle">Valide jusqu'au</dt>
        <dd className="text-ink">
          {quote.valid_until ? formatDate(quote.valid_until) : "non précisé"}
        </dd>
        <dt className="text-ink-subtle">Devise</dt>
        <dd className="text-ink">{quote.currency ?? "non précisée"}</dd>
        <dt className="text-ink-subtle">Demande de prix</dt>
        <dd className="text-ink">
          {quote.rfq_id ? (
            <Link to={`/rfqs/${quote.rfq_id}`} className="font-mono hover:text-brand">
              {quote.rfq_code ?? "voir"}
            </Link>
          ) : (
            "aucune"
          )}
        </dd>
        {quote.revision > 1 && (
          <>
            <dt className="text-ink-subtle">Révision</dt>
            <dd className="text-ink">
              n° {quote.revision}
              {quote.revises_quote_id && (
                <Link
                  to={`/quotes/${quote.revises_quote_id}`}
                  className="ml-2 text-[12px] hover:text-brand"
                >
                  voir la précédente
                </Link>
              )}
            </dd>
          </>
        )}
      </dl>
    </section>
  );
}

/**
 * Les deux totaux, côte à côte.
 *
 * Quand le fournisseur et le calcul ne disent pas la même chose, **on montre
 * les deux et l'écart**. Corriger l'un des deux en silence serait décider à la
 * place de quelqu'un qui doit appeler le fournisseur.
 */
function Totals({ totals, quote }: { totals: QuoteTotals; quote: SupplierQuote }) {
  const currency = quote.currency ?? "";
  return (
    <section className="flex min-w-0 flex-col gap-2">
      <h2 className="text-[15px] font-medium text-ink">Montants</h2>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 rounded-lg border border-border bg-surface p-3 text-[13px]">
        <dt className="text-ink-subtle">Total des lignes</dt>
        <dd className="tnum text-right text-ink">
          {totals.lines_total} {currency}
        </dd>
        <dt className="text-ink-subtle">Transport</dt>
        <dd className="tnum text-right text-ink">
          {totals.freight === null ? (
            <span className="text-warning">{formatFreightMode(quote.freight_mode)}</span>
          ) : (
            `${totals.freight} ${currency}`
          )}
        </dd>
        <dt className="text-ink-subtle">Taxes</dt>
        <dd className="tnum text-right text-ink">
          {totals.tax === null ? "non précisées" : `${totals.tax} ${currency}`}
        </dd>
        <dt className="font-medium text-ink">Calculé</dt>
        <dd className="tnum text-right font-medium text-ink">
          {totals.calculated_total} {currency}
        </dd>
        {totals.supplier_total !== null && (
          <>
            <dt className="text-ink-subtle">Annoncé par le fournisseur</dt>
            <dd className="tnum text-right text-ink">
              {totals.supplier_total} {currency}
            </dd>
          </>
        )}
        {!totals.agrees && totals.difference !== null && (
          <>
            <dt className="font-medium text-warning">Écart</dt>
            <dd className="tnum text-right font-medium text-warning">
              {totals.difference} {currency}
            </dd>
          </>
        )}
      </dl>
      {!totals.agrees && (
        <p className="text-[12px] text-ink-muted">
          Les deux chiffres sont conservés tels quels. Rien n'a été corrigé : appelez le
          fournisseur pour savoir lequel vaut.
        </p>
      )}
    </section>
  );
}

function LinesSection({
  quoteId,
  quote,
  lines,
  editable,
}: {
  quoteId: string;
  quote: SupplierQuote;
  lines: SupplierQuoteLine[];
  editable: boolean;
}) {
  return (
    <section className="flex min-w-0 flex-col gap-2">
      <h2 className="text-[15px] font-medium text-ink">Lignes chiffrées</h2>

      {lines.length === 0 ? (
        <EmptyState
          title="Aucune ligne"
          description="Lisez le document, ou saisissez ce que le fournisseur a dit."
        />
      ) : (
        <>
          <div className="hidden overflow-hidden rounded-lg border border-border bg-surface md:block">
            <TableShell>
              <thead>
                <tr>
                  <Th className="max-w-0">Offert</Th>
                  <Th className="hidden whitespace-nowrap @4xl:table-cell">SKU / MPN</Th>
                  <Th align="right" className="whitespace-nowrap">
                    Demandé
                  </Th>
                  <Th align="right" className="whitespace-nowrap">
                    Chiffré
                  </Th>
                  <Th align="right" className="whitespace-nowrap">
                    Prix unitaire
                  </Th>
                  <Th align="right" className="whitespace-nowrap">
                    Sous-total
                  </Th>
                  <Th className="whitespace-nowrap">Disponibilité</Th>
                  <Th align="right" className="whitespace-nowrap">
                    Délai
                  </Th>
                  <Th>Rapprochement</Th>
                  {editable && <Th className="w-24" />}
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => (
                  <LineRow
                    key={line.id}
                    quoteId={quoteId}
                    quote={quote}
                    line={line}
                    editable={editable}
                  />
                ))}
              </tbody>
            </TableShell>
          </div>

          <ul className="flex flex-col gap-2 md:hidden">
            {lines.map((line) => (
              <li key={line.id}>
                <LineCard quote={quote} line={line} />
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function LineRow({
  quoteId,
  quote,
  line,
  editable,
}: {
  quoteId: string;
  quote: SupplierQuote;
  line: SupplierQuoteLine;
  editable: boolean;
}) {
  const toast = useToast();
  const update = useUpdateQuoteLine();

  return (
    <Tr>
      <Td className="max-w-0">
        <span className="block truncate font-medium text-ink">{line.description}</span>
        <span className="flex flex-wrap items-center gap-1.5">
          <StatusBadge tone={lineResponseTone(line.response)}>
            {formatLineResponse(line.response)}
          </StatusBadge>
          {line.warnings.map((warning) => (
            <span key={warning} className="text-[11px] text-warning">
              {formatQuoteWarning(warning)}
            </span>
          ))}
        </span>
      </Td>
      <Td className="hidden whitespace-nowrap font-mono text-[11px] text-ink-muted @4xl:table-cell">
        {line.supplier_sku ?? "—"}
        {line.mpn ? ` / ${line.mpn}` : ""}
      </Td>
      <Td align="right" className="tnum whitespace-nowrap text-[13px] text-ink-muted">
        {line.requested_quantity ? formatQuantity(line.requested_quantity) : "—"}
      </Td>
      <Td align="right" className="tnum whitespace-nowrap text-[13px] text-ink">
        {line.quoted_quantity ? formatQuantity(line.quoted_quantity) : "—"}
        {line.unit ? ` ${line.unit}` : ""}
      </Td>
      <Td align="right" className="tnum whitespace-nowrap text-[13px] text-ink">
        {/* Absence de prix : un tiret, jamais 0,00. */}
        {line.unit_price ?? "—"}
      </Td>
      <Td align="right" className="tnum whitespace-nowrap text-[13px] text-ink">
        {line.line_subtotal ?? line.computed_subtotal ?? "—"}
        {line.subtotal_difference && line.subtotal_difference !== "0.00" && (
          <span className="block text-[11px] text-warning">
            calculé {line.computed_subtotal}
          </span>
        )}
      </Td>
      <Td className="whitespace-nowrap text-[12px] text-ink-muted">
        {formatAvailability(line.response, quote.quote_date, quote.received_at)}
      </Td>
      <Td align="right" className="tnum whitespace-nowrap text-[12px] text-ink-muted">
        {line.lead_time_days === null ? "—" : `${line.lead_time_days} j`}
      </Td>
      <Td className="text-[12px] text-ink-muted">
        {line.product_name ?? "aucun article"}
        <span className="block text-[11px] text-ink-subtle">
          {formatMatchConfidence(line.match_confidence)}
        </span>
      </Td>
      {editable && (
        <Td>
          <Button
            variant="secondary"
            size="sm"
            disabled={line.reviewed || update.isPending}
            onClick={() =>
              void update
                .mutateAsync({ quoteId, lineId: line.id, reviewed: true })
                .catch((failure: unknown) => toast.error("Non enregistré", String(failure)))
            }
          >
            {line.reviewed ? "Vue" : "Marquer vue"}
          </Button>
        </Td>
      )}
    </Tr>
  );
}

function LineCard({ quote, line }: { quote: SupplierQuote; line: SupplierQuoteLine }) {
  return (
    <div className="flex flex-col gap-1 rounded-lg border border-border bg-surface p-3">
      <p className="truncate text-[14px] font-medium text-ink">{line.description}</p>
      <div className="flex flex-wrap items-center gap-1.5">
        <StatusBadge tone={lineResponseTone(line.response)}>
          {formatLineResponse(line.response)}
        </StatusBadge>
        {line.product_name && (
          <span className="text-[11px] text-ink-subtle">{line.product_name}</span>
        )}
      </div>
      <p className="tnum text-[13px] text-ink-muted">
        {line.quoted_quantity ? formatQuantity(line.quoted_quantity) : "—"}
        {line.unit ? ` ${line.unit}` : ""} ·{" "}
        {line.unit_price ? `${line.unit_price} ${line.currency ?? quote.currency ?? ""}` : "sans prix"}
      </p>
      <p className="text-[12px] text-ink-subtle">
        {formatAvailability(line.response, quote.quote_date, quote.received_at)}
      </p>
      {line.warnings.map((warning) => (
        <p key={warning} className="text-[11px] text-warning">
          {formatQuoteWarning(warning)}
        </p>
      ))}
    </div>
  );
}

/**
 * Les lignes demandées restées sans réponse.
 *
 * **Aucun prix de zéro n'est créé pour elles.** Une ligne sans réponse est une
 * question ouverte, pas une offre gratuite.
 */
function Unanswered({ detail }: { detail: SupplierQuoteDetail }) {
  return (
    <section className="flex min-w-0 flex-col gap-2">
      <h2 className="text-[15px] font-medium text-ink">Sans réponse</h2>
      <p className="text-[13px] text-ink-muted">
        {pluralize(detail.unanswered.length, "ligne")} de la demande de prix n'{" "}
        {detail.unanswered.length > 1 ? "ont" : "a"} reçu aucune réponse de ce
        fournisseur. Ce n'est pas un prix de zéro.
      </p>
      <ul className="flex flex-col gap-1 rounded-lg border border-border bg-surface p-3 text-[13px]">
        {detail.unanswered.map((line) => (
          <li key={line.rfq_line_id} className="flex justify-between gap-3">
            <span className="min-w-0 truncate text-ink">{line.description}</span>
            <span className="tnum whitespace-nowrap text-ink-muted">
              {formatQuantity(line.requested_quantity)} demandé
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** D'où vient chaque valeur, et qui l'a confirmée. */
function ProvenancePanel({ quoteId }: { quoteId: string }) {
  const [open, setOpen] = useState(false);
  const { data } = useQuoteProvenance(quoteId, { enabled: open });

  return (
    <section className="flex min-w-0 flex-col gap-2">
      <details
        className="rounded-lg border border-border bg-surface p-3 text-[13px]"
        onToggle={(event) => setOpen((event.target as HTMLDetailsElement).open)}
      >
        <summary className="cursor-pointer text-ink-muted">
          Provenance des valeurs
        </summary>
        {data && data.length === 0 && (
          <p className="mt-2 text-ink-subtle">Aucune provenance enregistrée.</p>
        )}
        {data && data.length > 0 && (
          <ul className="mt-2 flex flex-col gap-1">
            {data.map((item) => (
              <li
                key={`${item.field_name}-${item.source_type}`}
                className="flex flex-wrap items-center gap-2 text-ink-muted"
              >
                <span className="font-mono text-[11px]">{item.field_name}</span>
                <span className="min-w-0 flex-1 truncate">{item.value_snapshot}</span>
                <StatusBadge tone={item.confirmed_by_human ? "available" : "warning"}>
                  {item.confirmed_by_human
                    ? `confirmé${item.confirmed_by ? ` par ${item.confirmed_by}` : ""}`
                    : `lu (${item.source_type.toLowerCase()})`}
                </StatusBadge>
              </li>
            ))}
          </ul>
        )}
      </details>
    </section>
  );
}

function RevisionButton({ quoteId, quote }: { quoteId: string; quote: SupplierQuote }) {
  const toast = useToast();
  const revise = useCreateQuoteRevision();
  if (quote.status === "REJECTED") return null;

  return (
    <Button
      variant="secondary"
      size="sm"
      disabled={revise.isPending}
      onClick={() =>
        void revise
          .mutateAsync({ quoteId })
          .then(() =>
            toast.success(
              "Révision ouverte",
              "L'ancienne version reste lisible telle qu'elle a été validée.",
            ),
          )
          .catch((failure: unknown) => toast.error("Révision impossible", String(failure)))
      }
    >
      <RefreshCw className="size-4" aria-hidden />
      Nouvelle révision
    </Button>
  );
}

/**
 * La confirmation de validation.
 *
 * Elle redit ce que valider veut dire — et surtout ce que ça **ne** veut pas
 * dire. C'est le moment où quelqu'un accepte qu'un chiffre entre dans les prix
 * de l'entreprise.
 */
function ValidateDialog({
  quoteId,
  detail,
  open,
  onOpenChange,
}: {
  quoteId: string;
  detail: SupplierQuoteDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const validate = useValidateQuote();
  const priced = detail.lines.filter(
    (line) =>
      (line.response === "QUOTED" || line.response === "SUBSTITUTE") &&
      line.product_id !== null &&
      line.unit_price !== null &&
      line.match_confidence === "CERTAIN",
  );

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Valider la soumission"
      description="Vous confirmez que ces chiffres sont bien ceux du fournisseur."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            variant="primary"
            disabled={validate.isPending}
            onClick={() =>
              void validate
                .mutateAsync({ quoteId })
                .then(() => {
                  toast.success("Soumission validée", "Son contenu est maintenant figé.");
                  onOpenChange(false);
                })
                .catch((failure: unknown) =>
                  toast.error("Validation impossible", String(failure)),
                )
            }
          >
            {validate.isPending ? "Validation…" : "Valider"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-[13px]">
        <p className="text-ink-muted">
          {pluralize(priced.length, "ligne")} deviendront une observation de prix
          fournisseur, datée du{" "}
          {detail.quote.quote_date
            ? formatDate(detail.quote.quote_date)
            : formatDate(detail.quote.received_at)}
          .
        </p>
        <p className="text-ink-muted">
          Valider <strong>ne veut pas dire</strong> : fournisseur choisi, achat approuvé,
          commande créée, ni marchandise disponible aujourd'hui.
        </p>
        {detail.quote.warnings.length > 0 && (
          <div className="flex flex-col gap-1 rounded-md border border-warning/30 bg-warning-subtle p-2.5">
            <p className="font-medium text-ink">Le contrôle a relevé :</p>
            <ul className="list-inside list-disc text-ink-muted">
              {detail.quote.warnings.map((warning) => (
                <li key={warning}>{formatQuoteWarning(warning)}</li>
              ))}
            </ul>
          </div>
        )}
        <p className="text-ink-muted">
          Après validation, le contenu est figé : une correction se fait par une
          révision.
        </p>
      </div>
    </Dialog>
  );
}

function RejectDialog({
  quoteId,
  open,
  onOpenChange,
}: {
  quoteId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const reject = useRejectQuote();

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Refuser la soumission"
      description="Elle restera lisible : savoir qu'un fournisseur a envoyé ceci est une information."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            variant="primary"
            disabled={reject.isPending}
            onClick={() =>
              void reject
                .mutateAsync({ quoteId })
                .then(() => {
                  toast.success("Soumission refusée");
                  onOpenChange(false);
                })
                .catch((failure: unknown) => toast.error("Refus impossible", String(failure)))
            }
          >
            Refuser
          </Button>
        </>
      }
    >
      <p className="text-[13px] text-ink-muted">
        Aucun prix ne sera créé, et rien ne sera supprimé.
      </p>
    </Dialog>
  );
}
